import { PropsWithChildren } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";

import { getGradient, ValidAngles } from "./gradients";

const positioner = {
    cornerTopLeft: "top left",
    cornerTopRight: "top right",
    cornerBottomLeft: "bottom left",
    cornerBottomRight: "bottom right",
    sideTop: "center top",
    sideLeft: "left center",
    sideRight: "right center",
    sideBottom: "center bottom",
    hidden: "-100% -100%"
} as const;

const sizes = {
    desktop: {
        cornerTopLeft: "400px auto",
        cornerTopRight: "400px auto",
        cornerBottomLeft: "400px auto",
        cornerBottomRight: "400px auto",
        sideTop: "auto 150px",
        sideLeft: "400px auto",
        sideRight: "400px auto",
        sideBottom: "auto 150px",
        hidden: "0 0"
    },
    tablet: {
        cornerTopLeft: "auto 50%",
        cornerTopRight: "auto 50%",
        cornerBottomLeft: "auto 50%",
        cornerBottomRight: "auto 50%",
        sideTop: "auto 150px",
        sideLeft: "calc(50% - 350px) auto",
        sideRight: "calc(50% - 350px) auto",
        sideBottom: "auto 150px",
        hidden: "0 0"
    },
    mobile: {
        cornerTopLeft: "auto 200px",
        cornerTopRight: "auto 200px",
        cornerBottomLeft: "auto 200px",
        cornerBottomRight: "auto 200px",
        sideTop: "auto 150px",
        sideLeft: "50%",
        sideRight: "50%",
        sideBottom: "auto 200px",
        hidden: "0 0"
    }
} as const;

export type ValidPositions =
    | "cornerTopLeft"
    | "cornerTopRight"
    | "cornerBottomLeft"
    | "cornerBottomRight"
    | "sideTop"
    | "sideLeft"
    | "sideRight"
    | "sideBottom"
    | "hidden";

export interface PositionProperties {
    imageLink: string;
    positionDesktop: ValidPositions;
    positionTablet: ValidPositions;
    positionMobile: ValidPositions;
}

export interface BGProps {
    isCompiled?: boolean; // not used in this file but kept while we still have BGStyles
    bgColor?: string;
    textColor?: string;
    gradientDirection?: ValidAngles;
    bgColor2?: string;
    bgImage?: string;
    partialBackgrounds?: PositionProperties[];
}

const getBGObject = (bg: PositionProperties) => {
    return {
        src: `url(${bg.imageLink})`,
        posDesktop: positioner[bg.positionDesktop],
        posTablet: positioner[bg.positionTablet],
        posMobile: positioner[bg.positionMobile],
        sizeDesktop: sizes.desktop[bg.positionDesktop],
        sizeTablet: sizes.tablet[bg.positionTablet],
        sizeMobile: sizes.mobile[bg.positionMobile]
    };
};

// eslint-disable-next-line complexity
export const StyledBackground = (
    backgroundProperties: PropsWithChildren<BGProps>
) => {
    const obj = backgroundProperties || {};

    const hasGradient = obj.gradientDirection && obj.bgColor && obj.bgColor2;

    const totalBGs = [];
    const collections = {
        desktop: {
            topCorners: false,
            bottomCorners: false
        },
        tablet: {
            topCorners: false,
            bottomCorners: false
        },
        mobile: {
            topCorners: false,
            bottomCorners: false
        }
    };

    if (hasGradient) {
        totalBGs.unshift({
            src: getGradient(obj.gradientDirection, obj.bgColor, obj.bgColor2),
            posDesktop: "top left",
            posTablet: "top left",
            posMobile: "top left",
            sizeDesktop: "100% 100%",
            sizeTablet: "100% 100%",
            sizeMobile: "100% 100%"
        });
    }

    if (obj.bgImage) {
        totalBGs.unshift({
            src: `url(${obj.bgImage} )`,
            posDesktop: "center center",
            posTablet: "center center",
            posMobile: "center center",
            sizeDesktop: "cover",
            sizeTablet: "cover",
            sizeMobile: "cover"
        });
    }

    if (obj.partialBackgrounds) {
        obj.partialBackgrounds.forEach((bg) => {
            totalBGs.unshift(getBGObject(bg));
            if (
                bg.positionDesktop.startsWith("cornerTop") ||
                bg.positionDesktop == "sideTop"
            ) {
                collections.desktop.topCorners = true;
            }
            if (
                bg.positionDesktop.startsWith("cornerBottom") ||
                bg.positionDesktop == "sideBottom"
            ) {
                collections.desktop.bottomCorners = true;
            }
            if (
                bg.positionTablet.startsWith("cornerTop") ||
                bg.positionTablet == "sideTop"
            ) {
                collections.tablet.topCorners = true;
            }
            if (
                bg.positionTablet.startsWith("cornerBottom") ||
                bg.positionTablet == "sideBottom"
            ) {
                collections.tablet.bottomCorners = true;
            }
            if (
                bg.positionMobile.startsWith("cornerTop") ||
                bg.positionMobile == "sideTop"
            ) {
                collections.mobile.topCorners = true;
            }
            if (
                bg.positionMobile.startsWith("cornerBottom") ||
                bg.positionMobile == "sideBottom"
            ) {
                collections.mobile.bottomCorners = true;
            }
        });
    }

    const backgroundImage = css({
        backgroundImage: totalBGs.map((bg) => bg.src).join(", "),
        backgroundRepeat: totalBGs.map(() => "no-repeat").join(", "),
        backgroundSize: totalBGs.map((bg) => bg.sizeMobile).join(", "),
        backgroundPosition: totalBGs.map((bg) => bg.posMobile).join(", "),

        [media.above.sm]: {
            backgroundSize: totalBGs.map((bg) => bg.sizeTablet).join(", "),
            backgroundPosition: totalBGs.map((bg) => bg.posTablet).join(", ")
        },

        [media.above.lg]: {
            backgroundSize: totalBGs.map((bg) => bg.sizeDesktop).join(", "),
            backgroundPosition: totalBGs.map((bg) => bg.posDesktop).join(", ")
        }
    });

    return (
        <div
            css={[
                obj.bgColor && css({ backgroundColor: obj.bgColor }),
                !obj.bgColor && css({ backgroundColor: "transparent" }),
                obj.textColor && css({ color: obj.textColor }),
                !obj.textColor && css({ textColor: "#091e42" }),
                (hasGradient || obj.bgImage || obj.partialBackgrounds) &&
                    backgroundImage,
                collections.mobile.topCorners &&
                    css({ paddingBlockStart: "200px" }),
                collections.mobile.bottomCorners &&
                    css({ paddingBlockEnd: "200px" }),
                collections.tablet.topCorners &&
                    css({ [media.above.sm]: { paddingBlockStart: "120px" } }),
                collections.tablet.bottomCorners &&
                    css({ [media.above.sm]: { paddingBlockEnd: "120px" } }),
                collections.desktop.topCorners &&
                    css({ [media.above.lg]: { paddingBlockStart: "120px" } }),
                collections.desktop.bottomCorners &&
                    css({ [media.above.lg]: { paddingBlockEnd: "120px" } })
            ]}
            className="styled-background"
        >
            {obj.children}
        </div>
    );
};
